<script setup>
import { computed } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'next/icon/Icon.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});

const { currentAccount, updateAccount } = useAccount();

const captainAiToggle = computed({
  get: () => currentAccount.value?.captain_ai_enabled ?? true,
  set: async enabled => {
    try {
      await updateAccount({ captain_ai_enabled: enabled });
    } catch (error) {
      useAlert(t('SIDEBAR.AI_CHAT.UPDATE_ERROR'));
    }
  },
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between gap-2 px-2 py-1.5">
      <div class="flex items-center gap-2 flex-grow min-w-0">
        <Icon
          icon="i-lucide-sparkles"
          class="size-4 text-n-slate-11 flex-shrink-0"
        />
        <span class="text-sm text-n-slate-12 truncate">{{
          $t('SIDEBAR.AI_CHAT.TEXT')
        }}</span>
      </div>
      <ToggleSwitch v-model="captainAiToggle" />
    </div>
    <DropdownContainer class="relative w-full min-w-0" @close="emit('close')">
      <template #trigger="{ toggle, isOpen }">
        <button
          class="flex gap-2 items-center p-1 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
          :class="{ 'bg-n-alpha-1': isOpen }"
          @click="toggle"
        >
          <Avatar
            :size="32"
            :name="currentUser.available_name"
            :src="currentUser.avatar_url"
            :status="currentUserAvailability"
            class="flex-shrink-0"
            rounded-full
          />
          <div class="min-w-0">
            <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
              {{ currentUser.available_name }}
            </div>
            <div class="text-xs truncate text-n-slate-11">
              {{ currentUser.email }}
            </div>
          </div>
        </button>
      </template>
      <DropdownBody class="bottom-12 z-50 mb-2 w-80 ltr:left-0 rtl:right-0">
        <SidebarProfileMenuStatus />
        <DropdownSeparator />
        <template v-for="item in allowedMenuItems" :key="item.label">
          <CustomBrandPolicyWrapper
            :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
          >
            <DropdownItem v-if="item.show" v-bind="item" />
          </CustomBrandPolicyWrapper>
        </template>
      </DropdownBody>
    </DropdownContainer>
  </div>
</template>
